import { AppView } from '../../types';

// ==========================================
// YAMOOPAY CORPORATE FINANCE & FP&A TYPES
// ==========================================

export type RevenueStreamType =
  | 'ADVERTISING'
  | 'AFFILIATE'
  | 'SUBSCRIPTION'
  | 'ONE_TIME_PURCHASE'
  | 'API_USAGE'
  | 'ENTERPRISE'
  | 'PARTNERSHIP';

export type RevenueSourceProvenance =
  | 'PROVIDER_REPORT'
  | 'BILLING_LEDGER'
  | 'ACCOUNTING_SYSTEM'
  | 'MANUAL_APPROVED';

export type PlanVersionType = 'BUDGET' | 'FORECAST' | 'LONG_RANGE_PLAN' | 'SCENARIO';
export type PlanStatus = 'DRAFT' | 'REVIEW' | 'APPROVED' | 'FROZEN' | 'SUPERSEDED';
export type ForecastScenarioType = 'BASE' | 'UPSIDE' | 'DOWNSIDE' | 'STRESS';
export type ControlType = 'PREVENTIVE' | 'DETECTIVE' | 'MANUAL' | 'AUTOMATED';
export type CashWarningLevel = 'NORMAL' | 'WATCH' | 'CONSTRAINED' | 'CRITICAL';

export interface FinanceCategory {
  code: string;
  name: string;
  type: 'REVENUE' | 'COGS' | 'OPEX' | 'CAPEX' | 'CASH' | 'ASSET' | 'LIABILITY' | 'MANAGEMENT_ONLY';
  description: string;
}

export interface CostCenter {
  code: string;
  name: string;
  department: string;
  owner: string;
  legalEntity: string;
}

export interface RevenueStream {
  id: string;
  name: string;
  type: RevenueStreamType;
  status: 'ACTIVE' | 'PLANNED' | 'PAUSED';
  mrrContributionMonthly: number;
  grossMarginPct: number;
  provenance: RevenueSourceProvenance;
  notes: string;
}

export interface FinanceMetric {
  key: string;
  name: string;
  category: 'REVENUE' | 'MARGIN' | 'OPEX' | 'CASH' | 'UNIT_ECONOMICS';
  value: number;
  unit: 'EUR' | 'USD' | 'PCT' | 'MONTHS' | 'COUNT';
  definition: string;
  formula: string;
  owner: string;
  provenance: string;
  updatedAt: string;
}

export interface ForecastScenario {
  id: string;
  name: string;
  type: ForecastScenarioType;
  trafficGrowthRatePct: number;
  adRpmAverageEur: number;
  affiliateConversionPct: number;
  hypotheticalPaidConversionPct: number;
  projectedAnnualRevenueEur: number;
  projectedGrossMarginPct: number;
  projectedMonthlyBurnEur: number;
  projectedRunwayMonths: number;
  isMonetizationMasterSwitchOn: boolean;
  notes: string;
}

export interface UnitEconomicsModel {
  id: string;
  segmentName: string;
  blendedCacEur: number;
  paidCacEur: number;
  ltv36MonthsEur: number;
  ltvToCacRatio: number;
  cacPaybackMonths: number;
  arpuMonthlyEur: number;
  arpaMonthlyEur: number;
  costPerCalculationEur: number;
  costPer1kApiCallsEur: number;
  notes: string;
}

export interface TreasuryAccount {
  id: string;
  bankName: string;
  legalEntity: string;
  currency: 'EUR' | 'USD' | 'CHF' | 'XOF' | 'GBP';
  accountType: 'OPERATING' | 'PAYMENT_PROCESSOR' | 'RESERVE' | 'TAX_HOLDING';
  balanceAmount: number;
  maskedIdentifier: string;
  lastReconciledAt: string;
  status: 'ACTIVE' | 'WATCH';
}

export interface CashForecastWeek {
  weekNumber: number;
  weekLabel: string;
  startingCashEur: number;
  projectedCollectionsEur: number;
  projectedPayrollsEur: number;
  projectedVendorOutflowsEur: number;
  projectedCloudFinOpsEur: number;
  endingCashEur: number;
  netBurnEur: number;
  warningLevel: CashWarningLevel;
}

export interface FinancialControl {
  id: string;
  code: string;
  objective: string;
  riskMitigated: string;
  owner: string;
  type: ControlType;
  frequency: 'DAILY' | 'MONTHLY_CLOSE' | 'QUARTERLY';
  lastExecutedDate: string;
  status: 'PASSED' | 'FAILED' | 'REVIEW_DUE';
}

export interface BoardFinancePack {
  id: string;
  period: string; // e.g. "Q3 2026"
  version: string;
  status: 'DRAFT' | 'REVIEW' | 'FROZEN';
  frozenBy?: string;
  frozenAt?: string;
  executiveSummary: string;
  actualRevenueEur: number;
  actualGrossMarginPct: number;
  actualCashBalanceEur: number;
  baseRunwayMonths: number;
  keyRisksAndMitigations: string[];
}

// ==========================================
// MOCK CORPORATE FINANCE DATA
// ==========================================

export const MOCK_FINANCE_CATEGORIES: FinanceCategory[] = [
  { code: 'REV-01', name: 'Advertising Revenue (AdSense / Media)', type: 'REVENUE', description: 'Monétisation affichage publicitaire sur comparateurs' },
  { code: 'REV-02', name: 'Affiliate Commissions (Money Transfers / Loans)', type: 'REVENUE', description: 'Commissions partenaires certifiées sur conversions' },
  { code: 'REV-03', name: 'Developer API Subscription & Overage', type: 'REVENUE', description: 'Abonnements API B2B (Master Switch Dependent)' },
  { code: 'COGS-01', name: 'Cloud Infrastructure & Serverless FinOps', type: 'COGS', description: 'Hébergement Cloud Run, réseaux et calcul déterministe' },
  { code: 'COGS-02', name: 'AI Model Inference & LLM Tokens', type: 'COGS', description: 'Costs for Gemini requests and deterministic RAG' },
  { code: 'OPEX-01', name: 'Engineering & R&D Payroll', type: 'OPEX', description: 'Masse salariale équipes Produit, Dev & SRE' },
  { code: 'OPEX-02', name: 'Corporate G&A, Legal & Compliance', type: 'OPEX', description: 'Frais de gouvernance, juridique et dépôts réglementaires' }
];

export const MOCK_COST_CENTERS: CostCenter[] = [
  { code: 'CC-100', name: 'Core Platform Engineering', department: 'Technology', owner: 'VP Platform Eng', legalEntity: 'YamooPay SAS (France)' },
  { code: 'CC-200', name: 'Financial Intelligence & Calculators', department: 'Product', owner: 'Principal PM', legalEntity: 'YamooPay SAS (France)' },
  { code: 'CC-300', name: 'Growth, SEO & Monetization Ops', department: 'Marketing', owner: 'Head of Growth', legalEntity: 'YamooPay SAS (France)' },
  { code: 'CC-400', name: 'Executive, Legal & Finance Ops', department: 'Corporate', owner: 'Chief Financial Officer', legalEntity: 'YamooPay SAS (France)' }
];

export const MOCK_REVENUE_STREAMS: RevenueStream[] = [
  {
    id: 'rev-ads',
    name: 'Advertising Monetization (Prompt 10)',
    type: 'ADVERTISING',
    status: 'ACTIVE',
    mrrContributionMonthly: 68500,
    grossMarginPct: 91.2,
    provenance: 'PROVIDER_REPORT',
    notes: 'Base sur 4.2M impressions mensuelles et EPMV de 16.30€'
  },
  {
    id: 'rev-affiliate',
    name: 'Affiliate Financial Partners (Prompt 10)',
    type: 'AFFILIATE',
    status: 'ACTIVE',
    mrrContributionMonthly: 84200,
    grossMarginPct: 88.5,
    provenance: 'PROVIDER_REPORT',
    notes: 'Commissions d\'affiliation certifiées sur transferts de fonds et crédits'
  },
  {
    id: 'rev-api-hypothetical',
    name: 'API Monetization B2B (Prompt 20)',
    type: 'API_USAGE',
    status: 'PLANNED',
    mrrContributionMonthly: 35000, // Scenario modeling
    grossMarginPct: 82.0,
    provenance: 'MANUAL_APPROVED',
    notes: 'SCÉNARIO HYPOTHÉTIQUE (Master Switch Paid Monetization = OFF)'
  }
];

export const MOCK_FINANCE_METRICS: FinanceMetric[] = [
  {
    key: 'ACTUAL_MRR',
    name: 'Monthly Run-Rate Revenue (Actual)',
    category: 'REVENUE',
    value: 152700,
    unit: 'EUR',
    definition: 'Revenu mensuel réel encaissable ou confirmé par rapports partenaires',
    formula: 'Ad Revenue Actuals + Affiliate Commissions Approved',
    owner: 'Financial Controller',
    provenance: 'FinanceDataMart.v1',
    updatedAt: '2026-08-25'
  },
  {
    key: 'GROSS_MARGIN_PCT',
    name: 'Gross Margin %',
    category: 'MARGIN',
    value: 86.4,
    unit: 'PCT',
    definition: 'Marge brute après déduction directe du FinOps Cloud et des jetons IA',
    formula: '(Revenue - COGS) / Revenue',
    owner: 'VP FP&A',
    provenance: 'FinanceDataMart.v1',
    updatedAt: '2026-08-25'
  },
  {
    key: 'CONTRIBUTION_MARGIN_PCT',
    name: 'Contribution Margin % (CM2)',
    category: 'MARGIN',
    value: 68.2,
    unit: 'PCT',
    definition: 'Marge de contribution après coûts d\'acquisition SEO et support client direct',
    formula: '(Gross Profit - Direct Marketing & Support) / Revenue',
    owner: 'VP FP&A',
    provenance: 'FinanceDataMart.v1',
    updatedAt: '2026-08-25'
  },
  {
    key: 'MONTHLY_NET_BURN',
    name: 'Monthly Net Burn',
    category: 'CASH',
    value: 42000,
    unit: 'EUR',
    definition: 'Déficit de trésorerie mensuel après dépenses d\'exploitation et salaires',
    formula: 'Operating Cash Outflows - Cash Collections',
    owner: 'Head of Treasury',
    provenance: 'TreasuryEngine.v1',
    updatedAt: '2026-08-25'
  },
  {
    key: 'CASH_RUNWAY_MONTHS',
    name: 'Cash Runway (Base Case)',
    category: 'CASH',
    value: 28.5,
    unit: 'MONTHS',
    definition: 'Autonomie en mois selon la trésorerie disponible et le burn moyen',
    formula: 'Available Cash Balance / Monthly Net Burn',
    owner: 'Head of Treasury',
    provenance: 'TreasuryEngine.v1',
    updatedAt: '2026-08-25'
  }
];

export const MOCK_FORECAST_SCENARIOS: ForecastScenario[] = [
  {
    id: 'scen-base',
    name: 'Scenario 1 — Base Case (Organic Execution)',
    type: 'BASE',
    trafficGrowthRatePct: 15.0,
    adRpmAverageEur: 16.5,
    affiliateConversionPct: 3.2,
    hypotheticalPaidConversionPct: 0.0,
    projectedAnnualRevenueEur: 2180000,
    projectedGrossMarginPct: 86.5,
    projectedMonthlyBurnEur: 38000,
    projectedRunwayMonths: 31.5,
    isMonetizationMasterSwitchOn: false,
    notes: 'Conservative assumption 100% based on organic Ads Hypothèse conservatrice 100% basée sur la croissance organique Ads & Affiliation Affiliation growth'
  },
  {
    id: 'scen-upside-paid',
    name: 'Scenario 2 — Upside (Active Paid Master Switch)',
    type: 'UPSIDE',
    trafficGrowthRatePct: 25.0,
    adRpmAverageEur: 18.2,
    affiliateConversionPct: 4.0,
    hypotheticalPaidConversionPct: 1.8,
    projectedAnnualRevenueEur: 3650000,
    projectedGrossMarginPct: 88.0,
    projectedMonthlyBurnEur: 22000,
    projectedRunwayMonths: 54.0,
    isMonetizationMasterSwitchOn: true,
    notes: 'Assumption with future activation of Paid Master Switch for B2B API Hypothèse avec activation future du Master Switch Paid pour API B2B & Abonnements Subscriptions'
  },
  {
    id: 'scen-stress-downside',
    name: 'Scenario 3 — Stress Test (Ad Market Drop)',
    type: 'STRESS',
    trafficGrowthRatePct: -5.0,
    adRpmAverageEur: 11.0,
    affiliateConversionPct: 2.1,
    hypotheticalPaidConversionPct: 0.0,
    projectedAnnualRevenueEur: 1250000,
    projectedGrossMarginPct: 81.0,
    projectedMonthlyBurnEur: 65000,
    projectedRunwayMonths: 18.4,
    isMonetizationMasterSwitchOn: false,
    notes: 'Severe 35% drop in global advertising CPM and cash stabilization'
  }
];

export const MOCK_UNIT_ECONOMICS: UnitEconomicsModel[] = [
  {
    id: 'ue-consumer-free',
    segmentName: 'Free Utility General Public (SEO Grand Public Utilité Gratuite (SEO & Organic) Organic)',
    blendedCacEur: 0.28, // SEO Content amortized cost per active user
    paidCacEur: 0.0,
    ltv36MonthsEur: 3.85,
    ltvToCacRatio: 13.75,
    cacPaybackMonths: 1.2,
    arpuMonthlyEur: 0.12,
    arpaMonthlyEur: 0.12,
    costPerCalculationEur: 0.0012,
    costPer1kApiCallsEur: 0.45,
    notes: 'Rentabilité organique exceptionnelle tirée par l\'utilité déterministe Prompt 14'
  },
  {
    id: 'ue-cross-border',
    segmentName: 'Cross-Border Transfrontaliers & Expatriés (High Monetization) Expatriates (High Monetization)',
    blendedCacEur: 1.45,
    paidCacEur: 4.20,
    ltv36MonthsEur: 24.50,
    ltvToCacRatio: 16.89,
    cacPaybackMonths: 2.1,
    arpuMonthlyEur: 0.78,
    arpaMonthlyEur: 0.78,
    costPerCalculationEur: 0.0028,
    costPer1kApiCallsEur: 0.85,
    notes: 'High residual value thanks to recurrence of tax simulations and transfers'
  }
];

export const MOCK_TREASURY_ACCOUNTS: TreasuryAccount[] = [
  {
    id: 'tr-01',
    bankName: 'BNP Paribas Corporate',
    legalEntity: 'YamooPay SAS (France)',
    currency: 'EUR',
    accountType: 'OPERATING',
    balanceAmount: 980000,
    maskedIdentifier: 'EUR-****-4821',
    lastReconciledAt: '2026-08-25 04:00',
    status: 'ACTIVE'
  },
  {
    id: 'tr-02',
    bankName: 'UBS Switzerland',
    legalEntity: 'YamooPay AG (Suisse - Future)',
    currency: 'CHF',
    accountType: 'RESERVE',
    balanceAmount: 220000,
    maskedIdentifier: 'CHF-****-9102',
    lastReconciledAt: '2026-08-25 04:00',
    status: 'ACTIVE'
  },
  {
    id: 'tr-03',
    bankName: 'Stripe Settlement Reserve',
    legalEntity: 'YamooPay SAS (France)',
    currency: 'EUR',
    accountType: 'PAYMENT_PROCESSOR',
    balanceAmount: 18500,
    maskedIdentifier: 'STRIPE-SETTLE-01',
    lastReconciledAt: '2026-08-25 05:00',
    status: 'ACTIVE'
  }
];

export const MOCK_CASH_FORECAST_13WEEKS: CashForecastWeek[] = [
  { weekNumber: 1, weekLabel: 'Semaine 35 (25-31 Aout)', startingCashEur: 1218500, projectedCollectionsEur: 38000, projectedPayrollsEur: 25000, projectedVendorOutflowsEur: 8000, projectedCloudFinOpsEur: 4500, endingCashEur: 1219000, netBurnEur: -500, warningLevel: 'NORMAL' },
  { weekNumber: 2, weekLabel: 'Semaine 36 (01-07 Sept)', startingCashEur: 1219000, projectedCollectionsEur: 12000, projectedPayrollsEur: 0, projectedVendorOutflowsEur: 12000, projectedCloudFinOpsEur: 4200, endingCashEur: 1214800, netBurnEur: 4200, warningLevel: 'NORMAL' },
  { weekNumber: 3, weekLabel: 'Semaine 37 (08-14 Sept)', startingCashEur: 1214800, projectedCollectionsEur: 42000, projectedPayrollsEur: 0, projectedVendorOutflowsEur: 6000, projectedCloudFinOpsEur: 4300, endingCashEur: 1246500, netBurnEur: -31700, warningLevel: 'NORMAL' },
  { weekNumber: 4, weekLabel: 'Semaine 38 (15-21 Sept)', startingCashEur: 1246500, projectedCollectionsEur: 15000, projectedPayrollsEur: 35000, projectedVendorOutflowsEur: 9000, projectedCloudFinOpsEur: 4400, endingCashEur: 1213100, netBurnEur: 33400, warningLevel: 'NORMAL' }
];

export const MOCK_FINANCIAL_CONTROLS: FinancialControl[] = [
  {
    id: 'ctrl-rev-rec',
    code: 'CTRL-FIN-01',
    objective: 'Réconciliation des revenus AdSense & Affiliation avec la comptabilité de gestion',
    riskMitigated: 'Erreur de surévaluation des revenus ou comptabilisation en double',
    owner: 'Financial Controller',
    type: 'AUTOMATED',
    frequency: 'MONTHLY_CLOSE',
    lastExecutedDate: '2026-08-20',
    status: 'PASSED'
  },
  {
    id: 'ctrl-segregation-adjust',
    code: 'CTRL-FIN-02',
    objective: 'Séparation des tâches : L\'émetteur d\'un ajustement comptable manuel ne peut le valider lui-même',
    riskMitigated: 'Fraude interne ou modification illégale des états financiers',
    owner: 'Chief Financial Officer',
    type: 'PREVENTIVE',
    frequency: 'DAILY',
    lastExecutedDate: '2026-08-24',
    status: 'PASSED'
  },
  {
    id: 'ctrl-paid-master-switch',
    code: 'CTRL-FIN-03',
    objective: 'Vérification du Master Switch Paid Monetization (Prompt 21) = OFF sur environnement Live',
    riskMitigated: 'Facturation accidentelle des utilisateurs sans activation commerciale validée',
    owner: 'Principal Finance Systems Architect',
    type: 'AUTOMATED',
    frequency: 'DAILY',
    lastExecutedDate: '2026-08-25',
    status: 'PASSED'
  }
];

export const MOCK_BOARD_PACK: BoardFinancePack = {
  id: 'pack-q3-2026',
  period: 'Q3 2026',
  version: 'v2.1-Certified',
  status: 'FROZEN',
  frozenBy: 'Chief Financial Officer',
  frozenAt: '2026-08-20 18:00',
  executiveSummary: 'YamooPay displays remarkable financial health driven by organic growth (+18% MoM). The economic model is 100% based on transparent Ads YamooPay affiche une santé financière remarquable portée par la croissance organique (+18% MoM). Le modèle économique repose à 100% sur la monétisation transparente Ads & Affiliation avec une marge brute de 86.4% et un Cash Runway supérieur à 28 mois. Affiliation monetization with an 86.4% gross margin and Cash Runway exceeding 28 months.',
  actualRevenueEur: 458100,
  actualGrossMarginPct: 86.4,
  actualCashBalanceEur: 1218500,
  baseRunwayMonths: 28.5,
  keyRisksAndMitigations: [
    'Volatilité du CPM publicitaire mondial -> Mitigation : Diversification sur les partenariats d\'affiliation bancaires',
    'Fluctuations des taux de change EUR/CHF -> Mitigation : Maintien de réserves de trésorerie multi-devises'
  ]
};

// ==========================================
// STORE CLASS
// ==========================================

class CorporateFinanceStore {
  private categories: FinanceCategory[] = [...MOCK_FINANCE_CATEGORIES];
  private costCenters: CostCenter[] = [...MOCK_COST_CENTERS];
  private revenueStreams: RevenueStream[] = [...MOCK_REVENUE_STREAMS];
  private metrics: FinanceMetric[] = [...MOCK_FINANCE_METRICS];
  private scenarios: ForecastScenario[] = [...MOCK_FORECAST_SCENARIOS];
  private unitEconomics: UnitEconomicsModel[] = [...MOCK_UNIT_ECONOMICS];
  private treasuryAccounts: TreasuryAccount[] = [...MOCK_TREASURY_ACCOUNTS];
  private cashForecast13W: CashForecastWeek[] = [...MOCK_CASH_FORECAST_13WEEKS];
  private controls: FinancialControl[] = [...MOCK_FINANCIAL_CONTROLS];
  private boardPack: BoardFinancePack = { ...MOCK_BOARD_PACK };

  // Getters
  getCategories() { return this.categories; }
  getCostCenters() { return this.costCenters; }
  getRevenueStreams() { return this.revenueStreams; }
  getMetrics() { return this.metrics; }
  getScenarios() { return this.scenarios; }
  getUnitEconomics() { return this.unitEconomics; }
  getTreasuryAccounts() { return this.treasuryAccounts; }
  getCashForecast13W() { return this.cashForecast13W; }
  getControls() { return this.controls; }
  getBoardPack() { return this.boardPack; }

  // Actions
  addScenario(newScenario: Omit<ForecastScenario, 'id'>) {
    const created: ForecastScenario = {
      ...newScenario,
      id: `scen-${Date.now()}`
    };
    this.scenarios.push(created);
    return created;
  }

  freezeBoardPack(frozenBy: string) {
    this.boardPack = {
      ...this.boardPack,
      status: 'FROZEN',
      frozenBy,
      frozenAt: new Date().toISOString().replace('T', ' ').substring(0, 16)
    };
    return this.boardPack;
  }
}

export const corporateFinanceStore = new CorporateFinanceStore();
